import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { updateMetaTags } from '../utils/seo';

function ResetPassword() {
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');
  const email = queryParams.get('email');

  useEffect(() => {
    updateMetaTags({
      title: 'Reset Password',
      description: 'Reset your Ortus FMCG account password.',
      siteName: 'Ortus FMCG'
    });

    if (!token || !email) {
      setError('Invalid reset link. Please request a new one.');
    }
  }, [token, email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password !== passwordConfirmation) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch((process.env.REACT_APP_API_URL || 'https://api.ortusfnb.com/api/v1') + '/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          token,
          password,
          password_confirmation: passwordConfirmation,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Reset failed');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <div className="absolute inset-0 opacity-60" style={{ background: 'radial-gradient(circle at center, rgba(16,185,129,0.2), transparent 70%)' }} />
        <div className="relative z-10 bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-50 rounded-full mb-6 text-red-600">
            <CheckCircle2 size={48} />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-3">Password Reset!</h2>
          <p className="text-slate-500 mb-8">Your password has been successfully updated. Redirecting you to login...</p>
          <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-red-500 animate-[progress_3s_linear]" style={{ width: '100%' }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-slate-950 overflow-hidden flex items-center justify-center px-4">
      <div className="absolute inset-0 opacity-60" style={{ background: 'radial-gradient(circle at top, rgba(16,185,129,0.35), transparent 55%)' }} />
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(15,118,110,0.12),rgba(8,47,73,0.65))]" />
      
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-10">
          <div className="mb-8">
            <p className="text-xs font-semibold tracking-[0.4em] text-red-600 uppercase">Security</p>
            <h2 className="text-3xl font-bold text-slate-900 mt-3">Reset Password</h2>
            <p className="text-sm text-slate-500 mt-2">Create a new secure password for your account.</p>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-600">Email Address</label>
              <input
                type="text"
                disabled
                value={email || ''}
                className="w-full rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-slate-500"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-semibold text-slate-600">
                New Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 pr-12 text-slate-900 focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="password_confirmation" className="text-sm font-semibold text-slate-600">
                Confirm New Password
              </label>
              <input
                id="password_confirmation"
                name="password_confirmation"
                type={showPassword ? 'text' : 'password'}
                required
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="••••••••"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !token || !email}
              className="w-full bg-slate-900 text-white font-semibold py-3 rounded-2xl shadow-lg shadow-red-500/20 hover:bg-slate-800 transition disabled:opacity-60"
            >
              {loading ? 'Updating...' : 'Reset password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
